<template>
    <div>
        <h1 class="text-3xl text-slate-800 font-semibold mb-5 leading-9">
            Setup Two Factor Authentication
        </h1>

        <div v-if="!hideVerifyCodeActions">
            <h2 class="text-slate-800 text-base font-medium mb-3">
                1 &ndash; Scan the QR code
            </h2>

            <p class="text-slate-800 text-sm mb-5">
                Scan this QR code in your authenticator app. We prefer
                <a
                    href="https://support.google.com/accounts/answer/1066447?hl=en&co=GENIE.Platform%3DiOS&sjid=5477005522162596228-NA"
                    class="link"
                    target="_blank"
                >
                    Google Authenticator app
                </a>
                .
            </p>

            <SafeQrCode
                class="flex justify-center mb-5 mx-auto py-3 text-center"
                :svg="qrCode"
            />

            <h2 class="text-slate-800 text-base font-medium mb-2">
                2 &ndash; Enter verification code
            </h2>
            <p class="text-slate-800 text-sm mb-4">
                Enter the code shown in your authenticator app
            </p>

            <VOtpInput
                ref="otpInput"
                input-classes="otp-input"
                inputType="numeric"
                :num-inputs="6"
                v-model:value="otpCode"
                :should-auto-focus="true"
                :should-focus-order="true"
                class="mb-5"
                @on-change="handleOnChange"
                @on-complete="registerStore.verifyCode(otpInput)"
            />
        </div>

        <div class="w-full max-w-md mx-auto" v-if="hideVerifyCodeActions">
            <p class="text-slate-800 text-sm font-medium mb-6">
                All done! You have confirmed your two factor authentication.
            </p>

            <p class="text-slate-800 text-sm mb-4">
                Please copy these recovery codes in case you ever need to
                recover your account and you no longer have access to your
                authenticator app.
            </p>

            <div
                class="pb-1 rounded-lg border border-slate-200 overflow-hidden mb-2"
            >
                <div class="flex items-start p-3 gap-2 bg-amber-50">
                    <Icon icon="caution" class="text-amber-500 mt-0.5" />
                    <p class="text-slate-800 text-sm font-medium leading-tight">
                        You will only see these codes once.
                        <br />
                        Store them in a safe place.
                    </p>
                </div>
                <ul class="flex flex-wrap py-4 px-2 gap-2 bg-white dark:bg-slate-800">
                    <li
                        class="w-[48%] text-slate-600 font-mono text-xs text-center"
                        v-for="code in recoveryCodes"
                    >
                        {{ code }}
                    </li>
                </ul>
            </div>

            <div class="flex items-center justify-between gap-4">
                <button
                    class="w-full flex items-center gap-2 justify-center px-4 py-2.5 bg-white dark:bg-slate-800 rounded shadow dark:shadow-slate-900/50 border border-slate-200 text-slate-800 text-sm font-medium hover:bg-slate-50 transition-colors"
                    @click="registerStore.copyCodes()"
                >
                    <Icon icon="copy" />
                    <span v-if="!copied">Copy</span>
                    <span v-else>Copied!</span>
                </button>
                <button
                    class="w-full flex items-center gap-2 justify-center px-4 py-2.5 bg-white dark:bg-slate-800 rounded shadow dark:shadow-slate-900/50 border border-slate-200 text-slate-800 text-sm font-medium hover:bg-slate-50 transition-colors"
                    @click="registerStore.downloadRecoveryCodes()"
                >
                    <Icon icon="download" />
                    Download .txt
                </button>
            </div>
        </div>
        <Errors :errors="errors" class="mb-6 pt-4" />
        <div
            class="flex items-center justify-between pt-4"
            v-if="!hideVerifyCodeActions"
        >
            <button
                class="px-4 py-2.5 bg-white dark:bg-slate-800 rounded shadow dark:shadow-slate-900/50 border border-slate-200 text-slate-800 text-sm font-medium hover:bg-slate-50 transition-colors"
                @click="registerStore.setShowCancel2faModal(true)"
            >
                Setup later
            </button>
            <button
                class="px-4 py-2.5 bg-slate-800 rounded shadow justify-center text-white text-sm font-medium hover:bg-slate-700 transition-colors"
                @click="registerStore.verifyCode(otpInput)"
            >
                <span v-if="!loading">Verify</span>
                <span v-else><Icon icon="loader" class="w-4 h-4" /></span>
            </button>
        </div>
        <div class="flex items-center pt-4" v-if="hideVerifyCodeActions">
            <button
                class="w-full px-4 py-2.5 bg-slate-800 rounded shadow justify-center text-white text-sm font-medium hover:bg-slate-700 transition-colors"
                @click="registerStore.completeTotpSetup()"
            >
                I've saved codes, continue
            </button>
        </div>
    </div>
</template>
<script setup>
import { storeToRefs } from 'pinia';
import { useRegisterStore } from '@/store/register';
import { ref } from 'vue';
import Errors from '@/components/errors.vue';
import Icon from '@/components/ui/icon/icon.vue';
import VOtpInput from 'vue3-otp-input';

const registerStore = useRegisterStore();
const {
    errors,
    qrCode,
    loading,
    hideVerifyCodeActions,
    recoveryCodes,
    copied,
} = storeToRefs(registerStore);

const otpCode = ref('');
const otpInput = ref(null);

const handleOnChange = (val) => {
    registerStore.setOtpCode(val);
};
</script>
